import moment from 'moment-timezone'
import { nanoid } from 'nanoid'

import { Periods } from 'constants/meetings'

/**
 * Determines if today should have a placeholder added based on filter period
 * @param {Object} filterParams - Filter parameters from useFilters
 * @param {string} todayStr - Today's date string in YYYY-MM-DD format
 * @returns {boolean} True if today placeholder should be added
 */
export const shouldAddTodayPlaceholder = (filterParams, todayStr) => {
  if (filterParams.period === Periods.FIXED) {
    const startDate = moment(filterParams.start_date)
    const endDate = moment(filterParams.end_date)
    const today = moment(todayStr)
    return today.isBetween(startDate, endDate, null, '[]')
  }
  return true
}

/**
 * Checks if there are any future meetings in the meetings data
 * @param {Array} meetingsData - Array of meeting groups
 * @param {string} todayStr - Today's date string in YYYY-MM-DD format
 * @returns {boolean} True if there are meetings after today
 */
export const hasFutureMeetings = (meetingsData, todayStr) => {
  if (!meetingsData?.length) return false
  return meetingsData.some((day) => day.rawDate > todayStr)
}

/**
 * Adds placeholder events for "today" when appropriate
 * @param {Array} meetingsData - Array of meeting groups
 * @param {string} todayStr - Today's date string in YYYY-MM-DD format
 * @param {boolean} addTodayPlaceholder - Whether to add today placeholder
 * @param {boolean} hasFutureMeetingsValue - Whether there are future meetings
 * @returns {Array} Meetings array with placeholder added if needed
 */
export const addTodayPlaceholderIfNeeded = (meetingsData, todayStr, addTodayPlaceholder, hasFutureMeetingsValue) => {
  if (!meetingsData?.length || !addTodayPlaceholder) return meetingsData

  // Find if we have meetings today
  const todayMeetings = meetingsData.find((day) => day.rawDate === todayStr)

  if (todayMeetings) {
    return meetingsData
  }

  const insertIndex = meetingsData.findIndex((day) => day.rawDate > todayStr)

  if (!hasFutureMeetingsValue) {
    return [
      ...meetingsData,
      {
        rawDate: todayStr,
        date: todayStr,
        events: [{
          placeholder: true,
          type: 'NO_FUTURE_MEETINGS',
          key: nanoid(6)
        }]
      }
    ]
  }

  // If insertIndex is -1, all meetings are in the past, but hasFutureMeetingsValue is true
  // This means future meetings exist elsewhere (e.g., in other pages). Insert at the end.
  const actualInsertIndex = insertIndex === -1 ? meetingsData.length : insertIndex

  return [
    ...meetingsData.slice(0, actualInsertIndex),
    {
      rawDate: todayStr,
      date: todayStr,
      events: [{
        placeholder: true,
        type: 'NO_TODAY_MEETINGS',
        key: nanoid(6)
      }]
    },
    ...meetingsData.slice(actualInsertIndex)
  ]
}

/**
 * When the user jumps to a specific date via the date picker and that date has no meetings,
 * insert an inline "No meetings this date" row so nearby dates remain scrollable (AC6).
 */
export const addEmptyDatePlaceholderIfNeeded = (meetingsData, targetDate) => {
  if (!targetDate || !meetingsData?.length) return meetingsData
  if (meetingsData.some((day) => day.rawDate === targetDate)) return meetingsData

  const insertIndex = meetingsData.findIndex((day) => day.rawDate > targetDate)
  const actualInsertIndex = insertIndex === -1 ? meetingsData.length : insertIndex

  return [
    ...meetingsData.slice(0, actualInsertIndex),
    {
      rawDate: targetDate,
      date: targetDate,
      events: [{
        placeholder: true,
        type: 'NO_MEETINGS_THIS_DATE',
        key: nanoid(6),
      }],
    },
    ...meetingsData.slice(actualInsertIndex),
  ]
}
